import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MenuItem } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { api } from '../api/client.js'
import { errorMessage } from '../api/errors.js'
import { tokens } from '../theme.js'
import { ProjectsBody, ProjectGrid, ProjectCard, NewProjectCard, ProjectName, ProjectMenuButton } from './styles/ProjectsPageStyles.js'
import { AppShell } from '../components/layout/AppShell.js'
import { PageTitle, Button, MutedText, ActionMenu, whenLabel, plural } from '../components/styles/index.js'
import { NameDialog, ConfirmDeleteDialog } from '../components/common/dialogs.js'

const colors = tokens.colors

export default function ProjectsPage() {
  const navigate = useNavigate()
  const [projects, setProjects] = useState(null)
  const [menu, setMenu] = useState(null) // { el, project }
  const [dialog, setDialog] = useState(null) // { kind: 'create' | 'rename' | 'delete', project? }
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    const { data } = await api.get('/api/projects')
    setProjects(data)
  }, [])
  useEffect(() => {
    load().catch(e => setError(errorMessage(e)))
  }, [load])

  async function create(name) {
    const { data } = await api.post('/api/projects', { name })
    setDialog(null)
    navigate(`/p/${data.id}`)
  }
  async function rename(name) {
    await api.patch(`/api/projects/${dialog.project.id}`, { name })
    setDialog(null)
    await load()
  }
  async function remove() {
    await api.delete(`/api/projects/${dialog.project.id}`)
    setDialog(null)
    await load()
  }

  const target = dialog?.project
  return (
    <AppShell crumbs={[{ label: 'Projects', testId: 'crumb-projects' }]}>
      <ProjectsBody>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
          <div>
            <PageTitle>Projects</PageTitle>
            <MutedText style={{ marginTop: 4, fontSize: 14 }} data-testid="projects-meta">
              {projects ? `${plural(projects.length, 'project')} · sorted by last activity` : 'Loading…'}
            </MutedText>
          </div>
          <Button onClick={() => setDialog({ kind: 'create' })} data-testid="new-project">
            <AddIcon /> New project
          </Button>
        </div>
        {error && <MutedText style={{ color: colors.error, marginTop: 12 }}>{error}</MutedText>}

        {projects && projects.length === 0 && (
          <MutedText style={{ marginTop: 28 }} data-testid="no-projects">
            No projects yet. Create one to start uploading and redacting documents.
          </MutedText>
        )}
        <ProjectGrid data-testid="project-list">
          {(projects || []).map(p => (
            <ProjectCard
              key={p.id}
              data-testid="project-card"
              data-project-name={p.name}
              $active={menu?.project.id === p.id}
              onClick={() => navigate(`/p/${p.id}`)}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <FolderOpenOutlinedIcon sx={{ fontSize: 22, color: colors.primary, marginTop: '1px' }} />
                <ProjectName data-testid="project-card-name">{p.name}</ProjectName>
              </div>
              <MutedText style={{ marginTop: 12, fontSize: 14 }}>
                {plural(p.fileCount ?? 0, 'file')}
                {p.folderCount ? ` · ${plural(p.folderCount, 'folder')}` : ''}
              </MutedText>
              <MutedText style={{ marginTop: 6, fontSize: 12 }}>
                {p.fileCount ? 'Updated' : 'Created'} {whenLabel(p.fileCount ? p.updatedAt : p.createdAt)}
              </MutedText>
              <ProjectMenuButton
                onClick={e => {
                  e.stopPropagation()
                  setMenu({ el: e.currentTarget, project: p })
                }}
                data-testid="project-card-menu"
                title="More">
                <MoreVertIcon fontSize="small" />
              </ProjectMenuButton>
            </ProjectCard>
          ))}
          {projects && (
            <NewProjectCard onClick={() => setDialog({ kind: 'create' })} data-testid="new-project-card">
              <AddIcon sx={{ fontSize: 24 }} /> New project
            </NewProjectCard>
          )}
        </ProjectGrid>

        <ActionMenu anchorEl={menu?.el} open={!!menu} onClose={() => setMenu(null)}>
          <MenuItem
            onClick={() => {
              setDialog({ kind: 'rename', project: menu.project })
              setMenu(null)
            }}
            data-testid="rename-project">
            <EditOutlinedIcon /> Rename
          </MenuItem>
          <MenuItem
            onClick={() => {
              setDialog({ kind: 'delete', project: menu.project })
              setMenu(null)
            }}
            data-testid="delete-project"
            className="danger">
            <DeleteOutlineIcon /> Delete…
          </MenuItem>
        </ActionMenu>

        <NameDialog
          open={dialog?.kind === 'create'}
          title="New project"
          label="Project name"
          placeholder="e.g. FOI-2026-0150 · Bylaw complaints"
          helper="You can add folders and upload files right after."
          confirmLabel="Create project"
          confirmTestId="create-project"
          inputTestId="project-name"
          onConfirm={create}
          onClose={() => setDialog(null)}
        />
        <NameDialog
          open={dialog?.kind === 'rename'}
          title="Rename project"
          label="Project name"
          initial={target?.name || ''}
          confirmLabel="Rename"
          confirmTestId="rename-confirm"
          inputTestId="project-name"
          onConfirm={rename}
          onClose={() => setDialog(null)}
        />
        <ConfirmDeleteDialog
          open={dialog?.kind === 'delete'}
          title="Delete project?"
          confirmLabel="Delete project"
          gate="DELETE"
          onConfirm={remove}
          onClose={() => setDialog(null)}>
          “{target?.name}” and its {plural(target?.fileCount ?? 0, 'file')}, {plural(target?.folderCount ?? 0, 'folder')}, redactions and results will
          be permanently deleted. This cannot be undone. Type DELETE to enable the button.
        </ConfirmDeleteDialog>
      </ProjectsBody>
    </AppShell>
  )
}
